// Sticker Studio: design a print-and-cut sticker, see it on the material, get the print sheet + cut file. Takes over the roll canvas area.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Download, Printer, Scissors, Undo2, Redo2, Save, FileText, Image as ImageIcon, Layers } from "lucide-react";
import { useWorkspace } from "../../store/WorkspaceContext";
import { useAuth } from "../../store/AuthContext";
import { addToRoll } from "../../engine/layout";
import { api, apiError } from "../../lib/api";
import { newSticker, newImage, typeOf, fmtIn } from "./stickerModel";
import { renderMask, exportPrintPng, exportPrintPdf, exportCutSvg, exportProofPng, thumbnail } from "./stickerExport";
import { cutLoops, sheetLayout } from "./stickerShapes";
import { StickerCanvas } from "./StickerCanvas";
import { StickerTools } from "./StickerTools";
import { StickerProps } from "./StickerProps";
import { LayerProps } from "./LayerProps";
import { AiArtDialog } from "./AiArtDialog";
import { Btn, FIELD } from "./ui";

const geomKey = (d) => JSON.stringify([d.w, d.h, d.offset, d.layers.map((l) => [l.kind, l.x, l.y, l.w, l.h, l.rot, l.size, l.text, l.font, l.weight, l.italic, l.spacing, l.strokeWidth, l.shape, l.radius, l.url, l.hidden, l.lineHeight])]);

export const StickerStudio = () => {
  const { state, dispatch } = useWorkspace();
  const { user } = useAuth();
  const open = state.stickerStudio;
  const [doc, setDocState] = useState(() => open?.doc || newSticker(open?.type));
  const hist = useRef({ past: [], future: [] });
  const [sel, setSel] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [ai, setAi] = useState(false);
  const [saved, setSaved] = useState(open?.doc ? "saved" : "new");
  const [menu, setMenu] = useState(false);
  const docRef = useRef(doc); docRef.current = doc;

  // set(fn, commit): commit=true pushes the doc as it was BEFORE this edit run onto the undo stack; commit=false is a live edit
  // (drag, slider, typing) — the first live edit remembers the base doc, the next commit pushes that base, so a whole run undoes at once.
  const liveBase = useRef(null);
  const set = useCallback((fn, commit = true) => {
    const d = docRef.current;  // bookkeeping happens here, synchronously — not inside the React updater, which runs later
    const n = typeof fn === "function" ? fn(d) : fn;
    if (!commit) { if (!liveBase.current) liveBase.current = d; }
    else {
      const base = liveBase.current || d;
      liveBase.current = null;
      if (n !== d || base !== d) { hist.current.past.push(base); hist.current.future = []; if (hist.current.past.length > 80) hist.current.past.shift(); }
    }
    if (n !== d) { docRef.current = n; setDocState(n); }
    setSaved("dirty");
  }, []);
  const changeDrag = useCallback((id, patch) => set((d) => ({ ...d, layers: d.layers.map((l) => (l.id === id ? { ...l, ...patch } : l)) }), false), [set]);
  const commit = useCallback(() => set((d) => d, true), [set]);
  const undo = () => { liveBase.current = null; const p = hist.current.past.pop(); if (!p) return; hist.current.future.push(docRef.current); docRef.current = p; setDocState(p); setSaved("dirty"); };
  const redo = () => { liveBase.current = null; const n = hist.current.future.pop(); if (!n) return; hist.current.past.push(docRef.current); docRef.current = n; setDocState(n); setSaved("dirty"); };

  // Contour cut line follows the artwork (debounced, traced on the server)
  const key = doc.shape === "contour" ? geomKey(doc) : null;
  useEffect(() => {
    if (!key || doc.cut?.key === key) return;
    let dead = false;
    const t = setTimeout(async () => {
      try {
        const { canvas, margin } = await renderMask(docRef.current, 40);
        const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
        const { pieces } = await api.stickerContour(blob, 40, docRef.current.offset, 0.1);
        if (dead) return;
        const shifted = pieces.map((L) => L.map(([x, y]) => [Math.round((x - margin) * 1000) / 1000, Math.round((y - margin) * 1000) / 1000]));
        setDocState((d) => ({ ...d, cut: { pieces: shifted, key } }));
      } catch (e) { if (!dead) console.warn("contour", e); }
    }, 450);
    return () => { dead = true; clearTimeout(t); };
  }, [key, doc.cut?.key]);

  // Autosave to the account
  useEffect(() => {
    if (saved !== "dirty") return;
    const t = setTimeout(async () => {
      try { const thumb = await thumbnail(docRef.current).catch(() => null); await api.saveStickerDesign(docRef.current.id, { name: docRef.current.name, doc: docRef.current, thumb }); setSaved("saved"); dispatch({ type: "LIBRARY_CHANGED" }); }
      catch (e) { setSaved("error"); }
    }, 1500);
    return () => clearTimeout(t);
  }, [saved, doc, dispatch]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest("input, textarea, select")) return;
      const l = docRef.current.layers.find((x) => x.id === sel);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); }
      else if (l && (e.key === "Delete" || e.key === "Backspace")) { set((d) => ({ ...d, layers: d.layers.filter((x) => x.id !== sel) })); setSel(null); }
      else if (l && (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "d") { e.preventDefault(); const c = { ...l, id: Math.random().toString(36).slice(2, 10), x: l.x + 0.25, y: l.y + 0.25 }; set((d) => ({ ...d, layers: [...d.layers, c] })); setSel(c.id); }
      else if (l && e.key.startsWith("Arrow")) { e.preventDefault(); const s = e.shiftKey ? 0.25 : 1 / 32; set((d) => ({ ...d, layers: d.layers.map((x) => (x.id === sel ? { ...x, x: x.x + (e.key === "ArrowRight" ? s : e.key === "ArrowLeft" ? -s : 0), y: x.y + (e.key === "ArrowDown" ? s : e.key === "ArrowUp" ? -s : 0) } : x)) })); }
      else if (e.key === "Escape") { if (sel) setSel(null); else close(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => dispatch({ type: "SET", patch: { stickerStudio: null } });
  const run = async (fn, label) => { setMenu(false); const id = toast.loading(`${label}…`); try { await fn(docRef.current); toast.success(`${label} ready`, { id }); } catch (e) { toast.error(apiError(e, `${label} failed`), { id }); } };
  const toRoll = () => {
    const loop = cutLoops(doc)[0];
    const xs = loop.map((p) => p[0]), ys = loop.map((p) => p[1]), x0 = Math.min(...xs), y0 = Math.min(...ys);
    const pts = loop.map(([x, y]) => [x - x0, y - y0]);
    const id = `sticker-${doc.id}`;
    const piece = { id, patternId: `sticker:${doc.id}`, decalId: id, name: doc.name, pieceType: "decal", side: "center", version: 1, ownerId: user?.id, setKey: "", setName: "", setOffset: null, multi: false, parts: 1, measure: { w: Math.max(...xs) - x0, h: Math.max(...ys) - y0 }, rawPoints: pts, points: pts, basePoints: pts, holes: [], x: 0, y: 0, rotation: 0, included: false, appliedOffset: 0, dirty: false, cleaned: true, autoFixed: true };
    const res = addToRoll([...state.pieces.filter((p) => p.id !== id), piece], [id], state);
    dispatch({ type: "SET_PIECES", pieces: res.pieces, commit: true });
    toast.success(`${doc.name}: cut line is on the roll`);
    setMenu(false); close();
  };
  const layer = doc.layers.find((l) => l.id === sel);
  const lay = useMemo(() => sheetLayout(doc), [doc]);
  const pieces = doc.shape === "contour" ? doc.cut?.pieces?.length || 0 : 1;
  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-[#0c1017] text-[#e6e8ec]" data-testid="sticker-studio">
      <div className="flex h-11 flex-none items-center gap-2 border-b border-[#1f2733] bg-[#121824] px-3">
        <button type="button" onClick={close} className="flex items-center gap-1 text-[12px] text-[#9aa0a8] hover:text-white" data-testid="sticker-studio-back"><ArrowLeft size={14} /> Graphics</button>
        <span className="mx-1 h-5 w-px bg-[#27324a]" />
        <input data-testid="sticker-name" value={doc.name} onChange={(e) => set((d) => ({ ...d, name: e.target.value }), false)} onBlur={() => set((d) => d)} className={`${FIELD} w-[220px] font-medium`} />
        <span className="text-[11px] text-[#6b7079]">{typeOf(doc).name} · {fmtIn(doc.w)} × {fmtIn(doc.h)} · {doc.copies} pcs · {lay.cells.length}/sheet</span>
        {doc.shape === "contour" && pieces > 1 && <span className="rounded border border-[#f5a524]/50 bg-[#f5a524]/10 px-2 py-0.5 text-[10px] text-[#ffd27a]" data-testid="sticker-pieces-warning">{pieces} separate pieces — raise the border or add a background shape for one sticker</span>}
        <span className="ml-auto text-[10px] text-[#6b7079]" data-testid="sticker-save-state">{saved === "saved" ? "Saved to your account" : saved === "dirty" ? "Saving…" : saved === "error" ? "Not saved — check connection" : "Not saved yet"}</span>
        <Btn onClick={undo} title="Undo (Ctrl+Z)" id="sticker-undo"><Undo2 size={13} /></Btn>
        <Btn onClick={redo} title="Redo (Ctrl+Y)" id="sticker-redo"><Redo2 size={13} /></Btn>
        <Btn onClick={() => setSaved("dirty")} title="Save now" id="sticker-save"><Save size={13} /></Btn>
        <div className="relative">
          <Btn tone="primary" onClick={() => setMenu((v) => !v)} id="sticker-print-menu" className="px-3"><Printer size={13} /> Print & cut files</Btn>
          {menu && (
            <div className="absolute right-0 top-full z-30 mt-1 w-[300px] rounded-md border border-[#27324a] bg-[#121824] p-1 shadow-2xl" data-testid="sticker-print-options">
              {[
                ["print-png", <ImageIcon size={13} />, "Print sheet · PNG 300 dpi", `${lay.sheet.name} with ${lay.cells.length} copies, bleed and registration marks`, () => run(exportPrintPng, "Print sheet PNG")],
                ["print-pdf", <FileText size={13} />, "Print sheet · PDF", "Same sheet as a true-size PDF for the print driver / RIP", () => run(exportPrintPdf, "Print sheet PDF")],
                ["cut-svg", <Scissors size={13} />, "Cut file · SVG", "CutContour paths + the same marks for Cutting Master, CutStudio, Summa, Silhouette…", () => run(async (d) => exportCutSvg(d), "Cut file")],
                ["proof", <Download size={13} />, "Proof · one sticker PNG", "Single sticker at 150 dpi with the cut line shown — for the customer to approve", () => run(exportProofPng, "Proof")],
                ["to-roll", <Layers size={13} />, "Cut line → Xpattern roll", "Put the contour on the roll to cut on your plotter (pre-printed vinyl, lined up by hand)", toRoll],
              ].map(([id, icon, t, s, fn]) => (
                <button key={id} type="button" data-testid={`sticker-out-${id}`} onClick={fn} className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left hover:bg-white/5">
                  <span className="mt-0.5 text-[#4c9ffe]">{icon}</span><span><span className="block text-[12px] text-white">{t}</span><span className="block text-[10px] leading-snug text-[#8b919c]">{s}</span></span>
                </button>
              ))}
              {lay.sheets > 1 && <div className="px-2 pb-1 pt-1 text-[10px] text-[#f5a524]">Print this sheet {lay.sheets}× for {doc.copies} stickers.</div>}
            </div>
          )}
        </div>
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="w-[230px] flex-none border-r border-[#1f2733] bg-[#0f141c]"><StickerTools doc={doc} set={set} sel={sel} setSel={setSel} openAi={() => setAi(true)} /></div>
        <div className="relative min-w-0 flex-1" onClick={() => menu && setMenu(false)}>
          <StickerCanvas doc={doc} sel={sel} setSel={setSel} change={changeDrag} commit={commit} zoom={zoom} setZoom={setZoom} />
          {ai && <AiArtDialog onClose={() => setAi(false)} onUse={(a) => { const l = newImage(docRef.current, a); set((d) => ({ ...d, layers: [...d.layers, l] })); setSel(l.id); }} />}
        </div>
        <div className="w-[300px] flex-none overflow-y-auto border-l border-[#1f2733] bg-[#0f141c]">
          {layer ? <LayerProps doc={doc} l={layer} set={set} setSel={setSel} /> : <StickerProps doc={doc} set={set} />}
        </div>
      </div>
    </div>
  );
};
